import React, { useState } from 'react';

import CRMApi from '../../../Api/CRM/CRMApi';
import LotteriesApi from '../../../Api/CRM/Modules/LotteriesApi';
import { TicketStatus } from '../../../Api/CRM/Responses/LotteryTicketResponse';
import LotteryTicket from '../../../Api/Models/LotteryTicket';
import Search from '../../elements/Search';
import Card from '../../elements/common/Card';
import FilterByColumn, { Filter } from '../../elements/common/FilterByColumn';
import TableLoader from '../../elements/common/TableLoader';
import { SelectOptions } from '../../elements/formElements/Select';
import Paginator from '../../elements/paginator';
import useAutoPaginator, { FetchPaginatorParams } from '../../hooks/useAutoPaginator';

import useOnce from '../../hooks/useOnce';

import TicketsTableDefault from './TicketsTableDefault';
import TicketsTableShort from './TicketsTableShort';

import DownloadButton from 'crm/elements/DownloadButton';
import { PERMISSION } from 'crm/helpers/Permissions';
import { createOptionsFromEnum } from 'crm/helpers/formUtils';

const ticketStatuses = createOptionsFromEnum(TicketStatus);

type Props = {
    short?: boolean;
    multidrawId?: number;
} & ({ source?: 'tickets'; relatedId?: never } | { source: 'reseller' | 'user'; relatedId: number });

function TicketsList({ source = 'tickets', relatedId, short, multidrawId }: Readonly<Props>) {
    const isMainSource = source === 'tickets';
    const [queryParams, setQueryParams] = useState<FetchPaginatorParams>();

    const { items, isLoading, auto } = useAutoPaginator<LotteryTicket>((page, params) => {
        setQueryParams(params);

        switch (source) {
            case 'user':
                return CRMApi.users.getTickets(relatedId, page, multidrawId, { params });
            case 'reseller':
                return CRMApi.resellers.getTickets(relatedId, page, multidrawId, { params });
            case 'tickets':
            default:
                return CRMApi.tickets.getAll(page, multidrawId, { params });
        }
    });

    const [lotteryNames, setLotteryNames] = useState<SelectOptions>({});

    useOnce(() => LotteriesApi.getNames().then(setLotteryNames));

    const tableHeader = (
        <>
            <span className="mr-3">{isMainSource ? 'All tickets' : 'Tickets'}</span>
            {isMainSource && (
                <DownloadButton
                    action="tickets-as-csv"
                    title="Export to CSV"
                    permissions={[PERMISSION.DOWNLOAD_TICKETS]}
                    defaultFilename="tickets.csv"
                    params={queryParams}
                />
            )}
        </>
    );

    return (
        <Card title={tableHeader}>
            <Search {...auto.search} />
            <FilterByColumn {...auto.filter}>
                {!short && <Filter column="lottery_id" label="Lottery" type="select" options={lotteryNames} />}
                <Filter column="status" type="select" options={ticketStatuses} />
                <Filter column="system_sell_amount_gross" label="Amount" type="numberRange" />
                {!short && [
                    <Filter key="A" column="lottery_tickets.draw_date" label="Draw date" type="dateRange" />,
                    <Filter key="B" column="lottery_tickets.created_at" label="Created at" type="dateRange" />,
                ]}
            </FilterByColumn>
            <TableLoader isLoading={isLoading}>
                {short ? (
                    <TicketsTableShort tickets={items} />
                ) : (
                    <TicketsTableDefault tickets={items} sortProps={auto.sort} />
                )}
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

export default TicketsList;
